import React from 'react';
import { Box } from '@mui/material';
import { Typography } from '../Typography';

/**
 * A single step shown in the {@link Stepper} progress bar.
 */
export interface StepperStep {
  /**
   * Stable key for the step, used as the React key
   */
  key: string;
  /**
   * The step's label
   */
  label: string;
}

export interface StepperProps {
  /**
   * The ordered steps to display
   */
  steps: StepperStep[];
  /**
   * Zero-based index of the currently active step. Its label is emphasised.
   */
  activeStep: number;
  /**
   * Zero-based index of the furthest step the user has reached. Steps up to and including this index render as
   * complete (a filled progress line) and — when `onStepSelect` is provided — are navigable; later steps render
   * incomplete (a grey line) and are not interactive. Defaults to `activeStep`, i.e. pure linear progress where
   * only the current and prior steps are complete.
   */
  furthestStep?: number;
  /**
   * Accessible label for the stepper list (translated by the caller)
   */
  ariaLabel: string;
  /**
   * Called when a navigable step is activated. When omitted, the stepper is purely presentational (no steps are
   * interactive). The active step and steps beyond `furthestStep` are never navigable.
   */
  onStepSelect?: (step: StepperStep, index: number) => void;
  /**
   * Test id applied to the stepper list
   */
  dataTestId?: string;
}

const INCOMPLETE_LINE_COLOUR = '#EAECF0';
const INACTIVE_LABEL_COLOUR = '#535862';

/**
 * Horizontal progress stepper that guides a user through an ordered, linear flow. Reached steps show a filled
 * progress line and the active step's label is emphasised; steps the user has not yet reached show a grey line.
 * When `onStepSelect` is provided, reached steps (other than the active one) become buttons so the user can
 * navigate back to a previous stage. The active step and not-yet-reached steps are rendered as plain,
 * non-interactive text so the only focusable controls are the steps a user can actually navigate to.
 */
export const Stepper: React.FC<StepperProps> = ({
  steps,
  activeStep,
  furthestStep,
  ariaLabel,
  onStepSelect,
  dataTestId = 'stepper',
}) => {
  const highestReached = furthestStep ?? activeStep;

  return (
    <Box
      component="ol"
      aria-label={ariaLabel}
      data-testid={dataTestId}
      sx={{ display: 'flex', gap: 2, listStyle: 'none', p: 0, m: 0 }}
    >
      {steps.map((step, index) => {
        const isActive = index === activeStep;
        const isReached = index <= highestReached;
        const isNavigable = Boolean(onStepSelect) && isReached && !isActive;

        const label = (
          <Typography
            variant="body2"
            sx={{ fontWeight: isActive ? 600 : 400, color: isActive ? 'primary.main' : INACTIVE_LABEL_COLOUR }}
          >
            {step.label}
          </Typography>
        );

        return (
          <Box
            component="li"
            key={step.key}
            aria-current={isActive ? 'step' : undefined}
            sx={(theme) => ({
              flex: 1,
              pt: 1.5,
              borderTop: '2px solid',
              borderTopColor: isReached ? theme.palette.primary.main : INCOMPLETE_LINE_COLOUR,
            })}
          >
            {isNavigable ? (
              <Box
                component="button"
                type="button"
                onClick={() => onStepSelect?.(step, index)}
                sx={{
                  appearance: 'none',
                  background: 'none',
                  border: 0,
                  p: 0,
                  m: 0,
                  font: 'inherit',
                  textAlign: 'left',
                  width: '100%',
                  color: 'inherit',
                  cursor: 'pointer',
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '2px',
                    borderRadius: '4px',
                  },
                }}
              >
                {label}
              </Box>
            ) : (
              label
            )}
          </Box>
        );
      })}
    </Box>
  );
};
